import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { handleBookingConfirmation } from '../../ApiHandler/PaymentFunctions';
import { checkPatientAddress } from '../../ApiHandler/PatientFunctions.js';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import '../Styles/Chatbot.css';
import PreBookingForm from '../Utils/PreBookingForm.jsx';
import ProviderSelection from './ProviderSelection.jsx';
// import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_API_URL;
const CHATBOT_URL = process.env.REACT_APP_CHATBOT_API_URL;

const INITIAL_BOT_MESSAGE = {
  type: 'bot',
  content: "This tool is not a substitute for professional medical advice, diagnosis, or treatment. Always consult a qualified health provider with questions about a medical condition.",
  options: ['Yes, I understand and consent', 'No, I want to exit']
};

const tagStyle = {
  display: 'inline-block',
  backgroundColor: '#e0f7fa',
  color: '#006064',
  padding: '4px 8px',
  margin: '4px 4px 0 0',
  borderRadius: '12px',
  fontSize: '0.9rem'
};
const SERVICE_SELECTION_MESSAGE = {
  type: 'bot',
  content: "Welcome to QuickCare! Which service would you like to use?",
  options: ['FAQ', 'Chatbot Analysis & Booking']
};

const FAQ_OPTIONS = {
  type: 'bot',
  content: "What would you like to learn about?",
  options: ["NP vs RN's", "Virtual vs At Home Care", "Start Over"]
};

const NP_VS_RN_INFO = {
  type: 'bot',
  content: "Nurse Practitioners (NPs) and Registered Nurses (RNs) have different roles in healthcare:",
  options: [
    "NP Services",
    "RN Services",
    "Back to FAQ Options"
  ]
};

const VIRTUAL_VS_HOME_INFO = {
  type: 'bot',
  content: "Let's explore the differences between virtual and at-home care:",
  options: [
    "Virtual Care Benefits",
    "At-Home Care Benefits",
    "Back to FAQ Options"
  ]
};

const markdownStyles = `
  .markdown-content {
    white-space: pre-wrap;
    line-height: 1.6;
  }
  
  .markdown-content h1, 
  .markdown-content h2, 
  .markdown-content h3 {
    margin-top: 1em;
    margin-bottom: 0.5em;
  }
  
  .markdown-content ul, 
  .markdown-content ol {
    margin-left: 1.5em;
    margin-bottom: 1em;
  }
  
  .markdown-content li {
    margin-bottom: 0.5em;
  }
  
  .markdown-content p {
    margin-bottom: 1em;
  }
  
  .markdown-content strong {
    font-weight: 600;
  }
  
  .markdown-content em {
    font-style: italic;
  }
  
  .markdown-content code {
    background-color: #f5f5f5;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
  }
  
  .markdown-content pre {
    background-color: #f5f5f5;
    padding: 1em;
    border-radius: 5px;
    overflow-x: auto;
  }
  
  .markdown-content blockquote {
    border-left: 4px solid #e0e0e0;
    margin-left: 0;
    padding-left: 1em;
    color: #666;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = markdownStyles;
document.head.appendChild(styleSheet);

const formatContent = (content) => {
  // Split content into lines
  const lines = content.split('\n');

  return lines.map((line, index) => {
    // Handle bullet points
    if (line.trim().startsWith('•')) {
      return (
        <div key={index} className="bullet-point">
          {line}
        </div>
      );
    }
    // Handle headers
    else if (line.startsWith('##')) {
      return (
        <h2 key={index} className="content-header">
          {line.replace('##', '').trim()}
        </h2>
      );
    }
    // Handle regular text
    else if (line.trim()) {
      return (
        <p key={index} className="content-text">
          {line}
        </p>
      );
    }
    // Handle empty lines
    return <br key={index} />;
  });
};

const Chatbot = ({ skipTokenValidation = false }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([SERVICE_SELECTION_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [hasConsented, setHasConsented] = useState(false);
  const [hasRisk, setHasRisk] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [hasBooking, setHasBooking] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [typeOfCare, setTypeOfCare] = useState('');
  const [serviceSelected, setServiceSelected] = useState(false);
  const [faqSelected, setFaqSelected] = useState(false);
  const [showProviderSelection, setShowProviderSelection] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({});
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [prebookingFormData, setPrebookingFormData] = useState({
    addressVerified: false,
    emergencyContact: '',
    allergies: '',
    chronicConditions: '',
    petsOnPremises: 'no',
    weaponsInHome: 'no',
    aggressionInHome: 'no',
    consentToTreat: false,
    medicationConsent: false,
    emergencyInterventionConsent: false,
    digitalSignature: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [isAddressComplete, setIsAddressComplete] = useState(false);
  const [address, setAddress] = useState([]);

  useEffect(() => {
    checkPatientAddress(setIsAddressComplete, setAddress);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    inputRef.current?.focus();
  }, [messages]);

  useEffect(() => {
    if (skipTokenValidation) return;

    if (!token) {
      navigate('/');
      return;
    }

    axios.post(`${API_URL}/auth/validate-chatbot-token`, { token })
      .then(res => {
        if (!res.data.valid) {
          navigate('/');
        }
      })
      .catch(err => {
        console.error("Invalid or expired token:", err);
        navigate('/');
      });
  }, [token, navigate]);

  // useEffect(() => {
  //   // If we have a selected provider, proceed with booking
  //   if (selectedProvider && hasBooking) {
  //     setShowBookingModal(true);
  //   }
  // }, [selectedProvider, hasBooking]);

  const get_educated = async (topic) => {
    try {
      const response = await axios.post(
        `${CHATBOT_URL}/api/education`,
        { topic },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setMessages(prev => [
          ...prev,
          {
            type: 'bot',
            content: response.data.content,
            options: response.data.options || []
          }
        ]);
      } else {
        throw new Error(response.data.error || 'Failed to get education content');
      }
    } catch (err) {
      console.error('Error getting education content:', err);
      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          content: "Sorry, I couldn't retrieve the education content. Please try again.",
          options: ["Back to FAQ Options"]
        }
      ]);
    }
  };

  const handleOptionSelect = (option) => {
    const lastMessage = messages[messages.length - 1];
    if (option.toLowerCase().includes('start over')) {
      handleStartOver();
      return;
    }

    // Handle Service Selection
    if (lastMessage.content === SERVICE_SELECTION_MESSAGE.content) {
      if (option === 'FAQ') {
        setMessages(prev => [FAQ_OPTIONS]);
        setFaqSelected(true);
        return;
      } else if (option === 'Chatbot Analysis & Booking') {
        setServiceSelected(true);
        setMessages([INITIAL_BOT_MESSAGE]);
        return;
      }
    }

    // Handle FAQ Options
    if (lastMessage.content === FAQ_OPTIONS.content) {
      if (option === "NP vs RN's") {
        setMessages(prev => [...prev, NP_VS_RN_INFO]);
        return;
      } else if (option === "Virtual vs At Home Care") {
        setMessages(prev => [...prev, VIRTUAL_VS_HOME_INFO]);
        return;
      }
    }

    // Handle Back to FAQ Options
    if (option === "Back to FAQ Options") {
      setMessages(prev => {
        const serviceSelectionIndex = prev.findIndex(msg =>
          msg.content === SERVICE_SELECTION_MESSAGE.content
        );
        return prev.slice(0, serviceSelectionIndex + 1).concat(FAQ_OPTIONS);
      });
      return;
    }

    // Handle NP vs RN specific options
    if (lastMessage.content === NP_VS_RN_INFO.content) {
      if (option === "NP Services") {
        get_educated("np_services");
        return;
      } else if (option === "RN Services") {
        get_educated("rn_services");
        return;
      } else if (option === "When to choose NP vs RN") {
        get_educated("np_vs_rn_comparison");
        return;
      } else if (option === "Scope of Practice") {
        get_educated("scope_of_practice");
        return;
      }
    }

    // Handle Virtual vs Home Care specific options
    if (lastMessage.content === VIRTUAL_VS_HOME_INFO.content) {
      if (option === "Virtual Care Benefits") {
        get_educated("virtual_care");
        return;
      } else if (option === "At-Home Care Benefits") {
        get_educated("home_care");
        return;
      } else if (option === "When to choose Virtual vs At-Home") {
        get_educated("care_comparison");
        return;
      } else if (option === "Cost Comparison") {
        get_educated("cost_comparison");
        return;
      }
    }
    if (faqSelected && option) {
      get_educated(option);
      return;
    }

    // Handle Consent or Exit at Disclaimer
    if (option === 'No, I want to exit') {
      handleStartOver();
      return;
    }

    if (option === 'Yes, I understand and consent') {
      const userConsentMessage = { type: 'user', content: option };
      const newHistory = [...messages, userConsentMessage];
      setMessages(newHistory);
      setHasConsented(true);
      sendMessage(option, newHistory);
      return;
    }

    // Handle Emergency Warning
    if (lastMessage.content === "Just checking in — if you are experiencing any of the symptoms below, please call 911 or contact a healthcare provider immediately. We're not equipped to help with emergencies.") {

      if (option !== 'I am not experiencing any of these symptoms') {
        handleStartOver();
        return;
      }
      sendMessage(option);
      return;
    }
    if (option === 'I am not experiencing any of these symptoms') {
      sendMessage(option);
      return;
    }

    // Handle Risk Confirmation
    if (lastMessage.content ===
      "It looks like your symptoms might be serious. Please consider calling 911 or contacting a healthcare provider immediately. This assistant is not equipped to handle emergencies.") {

      if (option === "I consent to continue") {
        sendMessage(option);
      } else {
        handleStartOver();
      }
      return;
    }

    // Other options like duration
    if (["Less than 24 hours", "1–3 days", "4–7 days", "More than a week"].includes(option)) {
      sendMessage(option);
      return;
    }

    // Start over

    if (option.toLowerCase() === "try again") {
      goToPreviousPhase();
      return;
    }
    // Multi-select options
    setSelectedOptions(prev =>
      prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
    );
  };

  const handleStartOver = () => {
    setMessages([SERVICE_SELECTION_MESSAGE]);
    setSelectedOptions([]);
    setUserInput('');
    setIsLoading(false);
    setHasConsented(false);
    setServiceSelected(false);
    setFaqSelected(false);
    setCurrentPhase(0);
  };

  const goToPreviousPhase = () => {
    const previousPhase = Math.max(currentPhase - 1, 1);
    const filtered = [INITIAL_BOT_MESSAGE];
    setMessages(filtered);
    setSelectedOptions([]);
    setUserInput('');
    setIsLoading(false);
    setFaqSelected(false);
    setCurrentPhase(previousPhase);
    sendMessage('', filtered); // re-trigger with empty to reload that step
  };

  const handleInputSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim() && selectedOptions.length === 0) return;

    const combinedMessage = [selectedOptions.join(', '), userInput.trim()].filter(Boolean).join(', ');

    if (combinedMessage.toLowerCase().includes('start over')) {
      handleStartOver();
      return;
    }

    setSelectedOptions([]);
    setUserInput('');
    await sendMessage(combinedMessage);
  };

  // const fetchProviders = async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const { data } = await axios.get(`${API_URL}/clinic/all-verified`, {
  //       headers: { Authorization: token }
  //     });
  //     if (data.success) setProviders(data.clinics);
  //   } catch (err) {
  //     toast.error('Failed to fetch providers');
  //   }
  // };

  // const handleBookingConfirmation = async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     if (!token) {
  //       navigate('/login');
  //       return;
  //     }

  //     const response = await axios.post(
  //       `${API_URL}/patient/confirm-booking`,
  //       {
  //         bookingDate: selectedDate,
  //         bookingTime: selectedTime,
  //       },
  //       {
  //         headers: {
  //           'Content-Type': 'application/json',
  //           'Authorization': token
  //         }
  //       }
  //     );

  //     if (response.data.success) {
  //       toast.success(`Booking confirmed with provider: ${response.data.assignedProvider.name}`, {
  //         position: "top-center"
  //       });
  //       setShowBookingModal(false);
  //     } else {
  //       toast.error(`Failed to confirm booking. Please try again.`, {
  //         position: "top-center"
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error confirming booking:', error);
  //     if (error.response?.status === 401) {
  //       localStorage.removeItem('token');
  //       navigate('/login');
  //     } else {
  //       toast.error(`Failed to confirm booking. Please try again.`, {
  //         position: "top-center"
  //       });
  //     }
  //   }
  // };

  const sendMessage = async (message, customHistory = null) => {
    setIsLoading(true);
    const userMsg = { type: 'user', content: message };
    const history = customHistory || [...messages, userMsg];
    if (!customHistory) setMessages(history);

    try {
      // First API call - Chat (Python server on port 8000)
      const { data } = await axios.post(
        `${CHATBOT_URL}/api/chat`,
        { message, history },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (data.success) {
        const botText = data.response;
        const opts = data.options || [];
        const cond = data.conditions;
        const ris = data.risk;
        const summ = data.summary;
        const book = data.booking;

        if (data.next_step) setCurrentPhase(parseInt(data.next_step));
        if (book === "book") setHasBooking(true);
        setHasRisk(ris === "risk");

        // Store summary if available (Node.js server on port 4500)
        if (summ && summ.length > 1) {
          try {
            const token = localStorage.getItem('token');
            if (!token) {
              console.error('No authentication token found');
              navigate('/login');
              return;
            }

            const storeResponse = await axios.post(
              `${API_URL}/patient/store-chatbot-session`,
              { analysisData: summ },
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': token
                }
              }
            );

            if (storeResponse.status !== 200) {
              console.error('Error storing session:', storeResponse.status, storeResponse.data);
            }
          } catch (storeError) {
            console.error('Error storing session:', storeError);
            if (storeError.response?.status === 401) {
              localStorage.removeItem('token');
              navigate('/login');
            }
          }
        }

        setMessages(prev => [
          ...prev,
          { type: 'bot', content: botText, options: opts, conditions: cond, risk: ris }
        ]);
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('Full error:', err);
        let errorMessage = "Oops, something went wrong. ";
        
        if (err.code === 'ERR_NETWORK') {
            errorMessage += "Unable to connect to the server. Please check if the server is running.";
            console.error('Server URL:', CHATBOT_URL); // Add this debug line
        } else if (err.code === 'ECONNABORTED') {
            errorMessage += "Request timed out. Please try again.";
        } 
      setMessages(prev => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg?.content?.includes("Oops, something went wrong")) return prev;
        return [
          ...prev,
          { type: 'bot', content: "Oops, something went wrong. Please try again or start over.", options: ["Start over"] }
        ];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSubmit = (e) => {
    e.preventDefault();
    if (String(selectedDate).trim() === '' || String(selectedTime).trim() === '' || String(typeOfCare).trim() === '') {
      toast.error('Please enter all the required fields', {
        position: 'top-center',
      });
      return;
    }
    setShowBookingModal(false);
    
    // Set booking details and show provider selection
    setBookingDetails({
      selectedDate,
      selectedTime,
      typeOfCare,
      prebookingFormData
    });
    setShowProviderSelection(true);
  };

  const handleProviderSelect = (provider) => {
    setShowProviderSelection(false);
    // Set the selected provider and proceed with booking
    setSelectedProvider(provider);
    setIsModalOpen(true);
  };

  const handleBackFromProviderSelection = () => {
    setShowProviderSelection(false);
    setShowBookingModal(true);
  };

  // const handleConfirmAction = () => {
  //   setIsModalOpen(false);
  //   if (modalAction) modalAction();
  // };

  // If showing provider selection, render that instead of chatbot
  if (showProviderSelection) {
    return (
      <ProviderSelection
        onProviderSelect={handleProviderSelect}
        onBack={handleBackFromProviderSelection}
        bookingDetails={bookingDetails}
      />
    );
  }

  return (
    <>
      <ToastContainer />
      <PreBookingForm
        isOpen={isModalOpen}
        isAddressComplete={isAddressComplete}
        address={address}
        formData={prebookingFormData}
        setFormData={setPrebookingFormData}
        onConfirm={() => {
          if (selectedProvider) {
            handleBookingConfirmation(prebookingFormData, selectedDate, selectedTime, typeOfCare, navigate, setIsModalOpen, setIsLoading, selectedProvider.clinic_id);
          } else {
            toast.error('Please select a provider first');
          }
        }}
        onCancel={() => setIsModalOpen(false)}
      />
      <div className="chatbot-app">
        <div className="chat-container">
          <div className="messages">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`message ${msg.type} ${msg.content === INITIAL_BOT_MESSAGE.content ? 'disclaimer-message' : ''}`}
              >
                <div className="message-content">
                  {msg.type === 'bot' ? (
                    <div className="formatted-content">
                      {formatContent(msg.content)}
                    </div>
                  ) : (
                    msg.content
                  )}
                  {msg.conditions && msg.conditions.length > 0 && (
                    <div className="pl-4 mb-2 mt-2">
                      {msg.conditions.map((condition, cIdx) => (
                        <p key={cIdx} className="ml-2 before:content-['•'] before:mr-2">
                          {'• ' + condition}
                        </p>
                      ))}
                    </div>
                  )}
                  {msg.options && msg.options.length > 0 && (
                    <div className="options">
                      {msg.options.map((opt, i) => (
                        <button
                          key={i}
                          className={`option-button ${selectedOptions.includes(opt) ? 'selected' : ''}`}
                          onClick={() => handleOptionSelect(opt)}
                          disabled={isLoading}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message bot">
                <div className="message-content">
                  <div className="typing-indicator"><span></span><span></span><span></span></div>
                </div>
              </div>
            )}
            {hasBooking && (
              <div className="booking-button-container">
                <button
                  className="booking-button"
                  onClick={() => setShowBookingModal(true)}
                >
                  Book Now
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {/* SEPARATE Selected Options Area */}
          {hasConsented && !hasRisk && (
            <>
              {selectedOptions.length > 0 && (
                <div className="selected-options">
                  <strong>Selected:</strong>
                  <div className="selected-tags">
                    {selectedOptions.map((opt, j) => (
                      <span key={j} style={tagStyle}>{opt}</span>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleInputSubmit} className="input-container">
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={e => setUserInput(e.target.value)}
                  placeholder={selectedOptions.length > 0 ? "Press Enter to submit selections..." : "Type your message..."}
                  disabled={isLoading}
                  className="chat-input wide"
                />
                <button
                  type="submit"
                  disabled={isLoading || (!userInput.trim() && selectedOptions.length === 0)}
                  className="send-button"
                >
                  Send
                </button>
              </form>
            </>
          )}
          {showBookingModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h2>Book Your Appointment</h2>
                <form>
                  <label>
                    Date:
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      required
                    />
                  </label>
                  <label>
                    Time Slot:
                    <input
                      type="time"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      required
                    />
                  </label>
                  <label>Type of Care</label>
                  <select
                    value={typeOfCare}
                    onChange={(e) => setTypeOfCare(e.target.value)}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Virtual Care">Virtual Care</option>
                    <option value="At Home Care">At Home Care</option>
                  </select>
                  <div className="modal-buttons">
                    <button type="button" className="confirm-button" onClick={handleConfirmSubmit} >Confirm Booking</button>
                    <button type="button" className="cancel-button" onClick={() => setShowBookingModal(false)}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Chatbot;